import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import {
  CheckCircle2,
  FileText,
  Globe,
  Loader2,
  Plus,
  RefreshCw,
  Upload,
} from 'lucide-react'
import {
  createScrapWebsite,
  createTextData,
  getFiles,
  uploadAssistantFile,
} from '@/services/assistant'
import { COLORS, INPUT_CLASS, INPUT_STYLE } from '../../shared'

const STATUS_LABELS = {
  READY_TO_PROCESS: 'Aguardando processamento',
  CONVERTING_TO_PDF: 'Convertendo',
  GENERATING_VECTORS: 'Treinando',
  SAVING_VECTORS: 'Salvando',
  DONE: 'Pronto',
  ERROR: 'Erro no processamento',
  UPLOADING: 'Enviando',
}

const SOURCE_OPTIONS = [
  { id: 'file', label: 'Documento', icon: FileText },
  { id: 'text', label: 'Texto', icon: Plus },
  { id: 'site', label: 'Site', icon: Globe },
]

function sourceName(item) {
  return item?.title || item?.name || 'Fonte de conhecimento'
}

function statusLabel(status) {
  return STATUS_LABELS[status] || 'Em preparação'
}

export default function KnowledgeSetup({ assistantID, onProgress, onTrack }) {
  const inputRef = useRef(null)
  const [source, setSource] = useState('file')
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [textForm, setTextForm] = useState({ title: '', text: '' })
  const [siteForm, setSiteForm] = useState({ title: '', url: '', recursive: false })

  const loadFiles = useCallback(async () => {
    if (!assistantID) return
    setLoading(true)
    try {
      const response = await getFiles({ assistantID, page: 1, perPage: 5 })
      const nextItems = response?.data || []
      const total = Number(response?.total)
      setItems(nextItems)
      onProgress?.({ knowledgeCount: Number.isFinite(total) ? total : nextItems.length })
    } catch (error) {
      console.error(error)
      toast.error('Não foi possível carregar as fontes de conhecimento.')
    } finally {
      setLoading(false)
    }
  }, [assistantID, onProgress])

  useEffect(() => {
    loadFiles()
  }, [loadFiles])

  async function handleFiles(fileList) {
    const files = Array.from(fileList || [])
    if (!files.length || !assistantID) return

    setUploading(true)
    const results = await Promise.allSettled(
      files.map((file) => uploadAssistantFile({ assistantID, file }))
    )
    const failed = results.filter((result) => result.status === 'rejected').length

    if (failed) {
      toast.error(
        failed === files.length
          ? 'Não foi possível enviar os documentos.'
          : `${failed} documento${failed > 1 ? 's' : ''} não pôde ser enviado.`
      )
    }
    if (failed !== files.length) {
      toast.success(
        `${files.length - failed} documento${files.length - failed > 1 ? 's' : ''} enviado${files.length - failed > 1 ? 's' : ''}.`
      )
      onTrack?.('onboarding_knowledge_added')
      await loadFiles()
    }

    setUploading(false)
  }

  async function submitText(event) {
    event.preventDefault()
    const title = textForm.title.trim()
    const text = textForm.text.trim()
    if (title.length < 3 || text.length < 10) {
      toast.error('Informe um título e um texto com pelo menos 10 caracteres.')
      return
    }

    setSubmitting(true)
    try {
      await createTextData({ assistantID, data: { title, text } })
      setTextForm({ title: '', text: '' })
      toast.success('Texto enviado para treinamento.')
      onTrack?.('onboarding_knowledge_added')
      await loadFiles()
    } catch (error) {
      console.error(error)
      toast.error('Não foi possível adicionar o texto.')
    } finally {
      setSubmitting(false)
    }
  }

  async function submitSite(event) {
    event.preventDefault()
    const title = siteForm.title.trim()
    const url = siteForm.url.trim()
    if (title.length < 3 || !/^https?:\/\/.+/i.test(url)) {
      toast.error('Informe um título e uma URL válida que comece com http:// ou https://.')
      return
    }

    setSubmitting(true)
    try {
      await createScrapWebsite({
        assistantID,
        data: { title, url, recursive: siteForm.recursive },
      })
      setSiteForm({ title: '', url: '', recursive: false })
      toast.success('Site enviado para treinamento.')
      onTrack?.('onboarding_knowledge_added')
      await loadFiles()
    } catch (error) {
      console.error(error)
      toast.error('Não foi possível adicionar o site.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-3 gap-2">
        {SOURCE_OPTIONS.map((option) => {
          const Icon = option.icon
          const selected = source === option.id
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => setSource(option.id)}
              className="flex min-h-16 flex-col items-center justify-center gap-1.5 rounded-xl px-2 py-3 text-center transition-colors"
              style={{
                background: selected ? 'rgba(52,173,254,0.13)' : 'rgba(255,255,255,0.025)',
                border: selected ? '1px solid rgba(52,173,254,0.5)' : '1px solid rgba(255,255,255,0.07)',
                color: selected ? '#34ADFE' : 'rgba(255,255,255,0.55)',
              }}
            >
              <Icon className="h-4 w-4" />
              <span className="text-[11px] font-semibold">{option.label}</span>
            </button>
          )
        })}
      </div>

      {source === 'file' && (
        <div
          className="flex flex-col items-center gap-3 rounded-2xl border border-dashed px-5 py-7 text-center"
          style={{ borderColor: 'rgba(52,173,254,0.42)', background: 'rgba(52,173,254,0.05)' }}
        >
          <input
            ref={inputRef}
            type="file"
            multiple
            accept=".pdf,.txt,.md,.mp3,.mpeg,.mp4"
            className="hidden"
            onChange={(event) => {
              handleFiles(event.target.files)
              event.target.value = ''
            }}
          />
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl" style={{ background: 'rgba(52,173,254,0.14)' }}>
            {uploading ? <Loader2 className="h-5 w-5 animate-spin text-[#34ADFE]" /> : <Upload className="h-5 w-5 text-[#34ADFE]" />}
          </span>
          <div>
            <p className="text-sm font-semibold" style={{ color: COLORS.text }}>
              Envie documentos para o agente aprender
            </p>
            <p className="mt-1 text-[11px] leading-relaxed" style={{ color: COLORS.textMuted }}>
              PDF, TXT, MD, MP3, MPEG ou MP4. O processamento continua em segundo plano.
            </p>
          </div>
          <button
            type="button"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
            className="flex h-10 items-center gap-2 rounded-xl px-4 text-xs font-semibold transition-opacity disabled:cursor-not-allowed disabled:opacity-50"
            style={{ background: 'rgba(52,173,254,0.14)', border: '1px solid rgba(52,173,254,0.35)', color: '#7CCBFF' }}
          >
            <Upload className="h-3.5 w-3.5" />
            Selecionar arquivos
          </button>
        </div>
      )}

      {source === 'text' && (
        <form className="flex flex-col gap-3" onSubmit={submitText}>
          <input
            value={textForm.title}
            onChange={(event) => setTextForm((current) => ({ ...current, title: event.target.value }))}
            placeholder="Título do conteúdo"
            className={INPUT_CLASS}
            style={INPUT_STYLE}
          />
          <textarea
            value={textForm.text}
            onChange={(event) => setTextForm((current) => ({ ...current, text: event.target.value }))}
            placeholder="Cole as informações que o agente deve conhecer..."
            className="min-h-28 w-full resize-y rounded-xl px-4 py-3 text-sm outline-none transition-all placeholder:text-white/20 focus:ring-2 focus:ring-[#34ADFE]/40"
            style={INPUT_STYLE}
          />
          <button
            type="submit"
            disabled={submitting}
            className="flex h-10 items-center justify-center gap-2 self-end rounded-xl px-4 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-50"
            style={{ background: 'rgba(52,173,254,0.14)', border: '1px solid rgba(52,173,254,0.35)', color: '#7CCBFF' }}
          >
            {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Adicionar texto
          </button>
        </form>
      )}

      {source === 'site' && (
        <form className="flex flex-col gap-3" onSubmit={submitSite}>
          <input
            value={siteForm.title}
            onChange={(event) => setSiteForm((current) => ({ ...current, title: event.target.value }))}
            placeholder="Título do site"
            className={INPUT_CLASS}
            style={INPUT_STYLE}
          />
          <input
            type="url"
            value={siteForm.url}
            onChange={(event) => setSiteForm((current) => ({ ...current, url: event.target.value }))}
            placeholder="https://suaempresa.com"
            className={INPUT_CLASS}
            style={INPUT_STYLE}
          />
          <label className="flex cursor-pointer items-start gap-2 text-[11px] leading-relaxed" style={{ color: COLORS.textMuted }}>
            <input
              type="checkbox"
              checked={siteForm.recursive}
              onChange={(event) => setSiteForm((current) => ({ ...current, recursive: event.target.checked }))}
              className="mt-0.5 h-3.5 w-3.5 accent-[#34ADFE]"
            />
            Buscar também links relacionados ao domínio.
          </label>
          <button
            type="submit"
            disabled={submitting}
            className="flex h-10 items-center justify-center gap-2 self-end rounded-xl px-4 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-50"
            style={{ background: 'rgba(52,173,254,0.14)', border: '1px solid rgba(52,173,254,0.35)', color: '#7CCBFF' }}
          >
            {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Adicionar site
          </button>
        </form>
      )}

      <div className="flex flex-col gap-2 rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="flex items-center justify-between gap-3">
          <span className="text-[11px] font-semibold" style={{ color: COLORS.text }}>
            Fontes recentes
          </span>
          <button
            type="button"
            onClick={loadFiles}
            disabled={loading}
            className="flex items-center gap-1 text-[10px] font-medium disabled:opacity-50"
            style={{ color: 'rgba(255,255,255,0.48)' }}
          >
            <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </div>
        {loading ? (
          <div className="h-10 animate-pulse rounded-lg bg-white/[0.05]" />
        ) : items.length === 0 ? (
          <p className="text-[11px] leading-relaxed" style={{ color: COLORS.textMuted }}>
            Nenhuma fonte adicionada ainda.
          </p>
        ) : (
          items.slice(0, 3).map((item) => (
            <div key={item.id} className="flex min-w-0 items-center gap-2 rounded-lg px-2 py-1.5" style={{ background: 'rgba(255,255,255,0.025)' }}>
              <FileText className="h-3.5 w-3.5 shrink-0 text-[#34ADFE]" />
              <span className="min-w-0 flex-1 truncate text-[11px]" style={{ color: COLORS.text }}>
                {sourceName(item)}
              </span>
              {item.status === 'DONE' ? (
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-[#34FEAD]" />
              ) : (
                <span className="shrink-0 text-[10px]" style={{ color: COLORS.textMuted }}>
                  {statusLabel(item.status)}
                </span>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  )
}
